import { ChangeEvent, Fragment, MouseEvent, SyntheticEvent, useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
// import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
// import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublicIcon from '@mui/icons-material/Public';
import { Menu, MenuItem, Tooltip, Typography } from '@mui/material';

import { queryAPI, useMount, useToggle } from '@cutwise/utils';

import { useTopicStore } from '@/dmdrive/topic/components/Topic/TopicStore';
import { ToolbarQuery } from '@/dmdrive/topic/constants/query';
import { Snapshot } from '@/dmdrive/topic/entities/Snapshot';
import { NotifyService } from '@/dmdrive/topic/services/NotifyService';
import { InputField } from '@/dmdrive/ui-kit/components';

import {
  SnapshotActions,
  SnapshotCaption,
  SnapshotContent,
  SnapshotPreview,
  SnapshotRowRoot,
  SnapshotTitle,
} from './SnapshotRow.styles';

interface SnapshotRowProps {
  snapshot: Snapshot;
}

function SnapshotRow(props: SnapshotRowProps) {
  const { snapshot } = props;
  const { isControlDisabled, setPresetFromSnapshot, updateSnapshot, deleteSnapshot } = useTopicStore();

  const inputRef = useRef<HTMLInputElement>(null);

  const [contextMenu, setContextMenu] = useState<Nullable<Record<'x' | 'y', number>>>(null);
  const [shouldRenderInput, showInput, hideInput] = useToggle(snapshot.isNew);
  const [title, setTitle] = useState(snapshot.title);

  const onChangeNameHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value), []);
  const onSelectHandler = useCallback(() => setPresetFromSnapshot(snapshot.id), [snapshot.id]);

  const onUpdateHandler = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();

      hideInput();
      updateSnapshot(snapshot.id, { ...snapshot.meta, title }, 'private');
    },
    [JSON.stringify(snapshot), title],
  );

  const onResetHandler = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();

      hideInput();
      setTitle(snapshot.title);
    },
    [snapshot.title, title],
  );

  const onDeleteHandler = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();

      deleteSnapshot(snapshot);
    },
    [JSON.stringify(snapshot)],
  );

  const onPublicHandler = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (snapshot.type === 'public') {
        snapshot.type = 'private';
        snapshot.meta.type = 'private';
        updateSnapshot(snapshot.id, snapshot.meta, 'private');
      } else if (snapshot.type === 'private') {
        snapshot.type = 'public';
        snapshot.meta.type = 'public';
        updateSnapshot(snapshot.id, snapshot.meta, 'public');
        onCopySnapshotLink();
      }
    },
    [JSON.stringify(snapshot)],
  );

  const onInputFocus = useCallback((e: SyntheticEvent) => {
    // prevent focus on MenuItem, otherwise it will have focus effect
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onFocusHandler = useCallback((e?: SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    showInput();

    // wait until input will be rendered
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, []);

  const onContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();

    setContextMenu(contextMenu === null ? { x: e.clientX + 2, y: e.clientY - 6 } : null);
  }, []);

  const onCloseContextMenu = useCallback(() => setContextMenu(null), []);

  const onCopySnapshotLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.href}${queryAPI.generateQuery({ ...queryAPI.params, [ToolbarQuery.Snapshot]: snapshot.id })}`,
      );

      onCloseContextMenu();

      NotifyService.success('Link copied');
    } catch (e) {
      NotifyService.error(e);
    }
  }, [snapshot.id]);

  useMount(() => {
    if (snapshot.isNew) {
      onFocusHandler();
    }
  });

  return (
    <Fragment>
      <SnapshotRowRoot
        disabled={isControlDisabled}
        onContextMenu={onContextMenu}
        onClick={onSelectHandler}
      >
        <SnapshotContent>
          <SnapshotPreview>
            <img src={snapshot.preview} alt="" />
          </SnapshotPreview>

          <SnapshotTitle>
            {shouldRenderInput && (
              <form onSubmit={onUpdateHandler}>
                <InputField
                  onFocus={onInputFocus}
                  ref={inputRef}
                  size="small"
                  value={title}
                  onChange={onChangeNameHandler}
                  isFullWidth
                  onBlur={onUpdateHandler}
                  onReset={onResetHandler}
                  onKeyDown={e => e.stopPropagation()} // Prevent key events from bubbling up
                />
              </form>
            )}

            {!shouldRenderInput && (
              <div>
                <Typography variant="inherit" noWrap>
                  {title}
                </Typography>

                <SnapshotCaption>
                  <Typography variant="caption">{snapshot.author}</Typography>
                </SnapshotCaption>
              </div>
            )}
          </SnapshotTitle>

            {!shouldRenderInput && snapshot.type === 'public' && (
             <SnapshotActions>
               <Tooltip title="Public">
                 <PublicIcon fontSize="small" color="action" />
               </Tooltip>
             </SnapshotActions>
           )}

        </SnapshotContent>
      </SnapshotRowRoot>

      <Menu
        open={contextMenu !== null}
        onClose={onCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu !== null ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        {snapshot.isMy && (
          <>
            <MenuItem onClick={e => { onPublicHandler(e); onCloseContextMenu(); }}>
              {snapshot.type === 'public' ? 'Make private' : 'Make public'}
            </MenuItem>
            </>
        )}
        
          <MenuItem onClick={() => { onCopySnapshotLink(); onCloseContextMenu(); }}>
            Copy link
          </MenuItem>

        {snapshot.isMy && (
          <>
          <MenuItem onClick={e => { onFocusHandler(e); onCloseContextMenu(); }}>
            Rename
          </MenuItem>
          <MenuItem onClick={e => { onDeleteHandler(e); onCloseContextMenu(); }}>
            Delete
          </MenuItem>
        </>
        )}
      </Menu>
    </Fragment>
  );
}

export default observer(SnapshotRow);
